/**
 * AOP 2027 Planning — a department's targets beside its plan (David, Oct 19 2026; reshaped Oct 26).
 *
 * Laid out so every total shows what it is made of (David, Oct 26 2026: it was not clear which
 * numbers make up the sums): FTE and cost each read top to bottom as committed, + AOP drafts,
 * = plan, budget, plan vs budget. Three tiles above sum up the plan year; an FY column closes the
 * table. The history months (Jan–Sep 2026) are left out here — the tree below still has them.
 *
 * Every figure is computed by the proxy (`proxy-server/lib/aopTargets.js`); this only lays it out
 * and adds months up. Its own table rather than rows inside `OrgChartTable`, which belongs to the
 * Org Planning Overview and is not changed for the AOP. FTE is a point in time (a column's last
 * month, December for the FY), cost the SUM of a column's months.
 */
import type { ReactNode } from 'react';
import type { AopDepartmentTargets } from '../../services/aopPlanningApi';
import type { MonthGroup } from '../internalPlayground/OrgChartTable';
import { formatEuro } from '../../types/compensation';
import { AOP_HISTORY_END, AOP_PLAN_YEAR } from './aopColumns';
import {
  costAt as costOf,
  costGap as costGapOf,
  fteAt,
  fteGap as fteGapOf,
  monthLabel,
  toColumns,
  yearColumn,
  type Column,
  type CostField,
  type FteField,
  type TargetsMonths,
} from './aopTargetsMath';

/** FTE to one decimal, '—' when there is none. */
const fte = (value: number | null | undefined) => (typeof value === 'number' ? value.toFixed(1) : '—');
const NUMBER = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
/** A KPI figure, up to two decimals, '—' when there is none. */
const plain = (value: number | null | undefined) => (typeof value === 'number' ? NUMBER.format(value) : '—');
/** A difference with its sign ("+1.0", "−30.000 €"), '—' when there is none. */
const signed = (value: number | null, format: (v: number) => string) =>
  value === null ? '—' : `${value > 0 ? '+' : value < 0 ? '−' : ''}${format(Math.abs(value))}`;
/** An FTE figure that is never blank, to one decimal. */
const fteText = (v: number) => v.toFixed(1);
type RowKind = 'part' | 'sum' | 'plain';

/** Red above budget, green below, plain on it or without one. */
const gapClass = (gap: number | null) =>
  gap === null || gap === 0 ? 'text-zinc-700' : gap > 0 ? 'text-red-600' : 'text-green-700';

/** "30.000 € over budget" / "1.0 under budget" / "on budget" / "no budget yet" / "no plan cost yet"
 * (`plan` names the missing figure). */
function gapNote(gap: number | null, hasBudget: boolean, plan: string, format: (v: number) => string) {
  if (gap === null) return hasBudget ? `no ${plan} yet` : 'no budget yet';
  if (gap === 0) return 'on budget';
  return `${format(Math.abs(gap))} ${gap > 0 ? 'over' : 'under'} budget`;
}

/** One summary figure above the table. */
function Tile({ label, value, note, noteClass = 'text-zinc-500' }: { label: string; value: string; note: string; noteClass?: string }) {
  return (
    <div className="rounded-md bg-zinc-50 px-3 py-2">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className="text-xl font-semibold tabular-nums text-zinc-900">{value}</p>
      <p className={`text-xs ${noteClass}`}>{note}</p>
    </div>
  );
}

/**
 * The tiles and the table for one set of figures — a department's (`AopTargetsTable`) or the
 * company's. `notes` go under the table; `kpis` add a KPI-per-FTE block.
 */
export function AopTargetsFigures({
  label,
  months,
  kpis = [],
  columns,
  groups,
  expandedGroupKeys,
  notes,
}: {
  label: string;
  months: TargetsMonths;
  kpis?: AopDepartmentTargets['kpis'];
  columns: string[];
  groups: MonthGroup[];
  expandedGroupKeys: Set<string>;
  notes?: ReactNode;
}) {
  const year = yearColumn(groups);
  const cols = [...toColumns(columns, groups, expandedGroupKeys), ...(year ? [year] : [])];
  /** A figure cell; the FY column is shaded. */
  const cellClass = (c: Column, tone: string) =>
    `border-l border-zinc-100 px-3 py-1 text-right text-sm tabular-nums ${c.total ? 'bg-zinc-50' : ''} ${tone}`;
  /** A cost row's figure over a column (see `aopTargetsMath.ts`). */
  const costAt = (c: Column, field: CostField) => costOf(months, c, field);
  /** Plan FTE minus budget FTE at a column's end month. */
  const fteGap = (c: Column) => fteGapOf(months, c);
  /** Plan cost minus budget cost over a column's months. */
  const costGap = (c: Column) => costGapOf(months, c);

  /** A section heading row, e.g. "Headcount (FTE, end of period)" — the first row of its own
   * `<tbody>`, so it heads that section's rows only. */
  const section = (name: string) => (
    <tr className="border-t border-zinc-200">
      <th
        scope="rowgroup"
        colSpan={cols.length + 1}
        className="bg-white px-3 pb-1 pt-3 text-left text-xs font-medium uppercase tracking-wide text-zinc-500"
      >
        {name}
      </th>
    </tr>
  );
  /** One row: a part of the sum is indented, the sum itself bold and ruled off. */
  const row = (name: string, kind: RowKind, cell: (c: Column) => { text: string; tone?: string }) => (
    <tr className={kind === 'sum' ? 'border-t border-zinc-200' : undefined}>
      <th
        scope="row"
        className={`sticky left-0 bg-white py-1 pr-3 text-left text-sm ${
          kind === 'part' ? 'pl-6 font-normal text-zinc-600' : kind === 'sum' ? 'pl-3 font-semibold text-zinc-900' : 'pl-3 font-normal text-zinc-600'
        }`}
      >
        {name}
      </th>
      {cols.map((c) => {
        const { text, tone } = cell(c);
        return (
          <td key={c.id} className={cellClass(c, tone ?? (kind === 'sum' ? 'font-semibold text-zinc-900' : 'text-zinc-700'))}>
            {text}
          </td>
        );
      })}
    </tr>
  );
  /** An FTE row, read at each column's end month. */
  const fteRow = (name: string, field: FteField, kind: RowKind) => row(name, kind, (c) => ({ text: fte(fteAt(months, c, field)) }));
  /** A cost row, summed over each column's months. */
  const costRow = (name: string, field: CostField, kind: RowKind) => row(name, kind, (c) => ({ text: formatEuro(costAt(c, field)) }));
  /** The signed "Plan vs budget" row, coloured by its sign. */
  const gapRow = (gapOf: (c: Column) => number | null, format: (v: number) => string) =>
    row('Plan vs budget', 'plain', (c) => ({ text: signed(gapOf(c), format), tone: gapClass(gapOf(c)) }));

  const yy = AOP_PLAN_YEAR.slice(2);
  const yearBudgetFte = year ? fteAt(months, year, 'budgetFte') : null;

  return (
    <section aria-label={`AOP targets for ${label}`} className="space-y-2">
      {year && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tile
            label={`Plan cost FY '${yy}`}
            value={formatEuro(costAt(year, 'planCost'))}
            note={gapNote(costGap(year), costAt(year, 'budgetCost') !== null, 'plan cost', formatEuro)}
            noteClass={gapClass(costGap(year))}
          />
          <Tile
            label={`Plan FTE ${monthLabel(year.end)}`}
            value={fte(fteAt(months, year, 'planFte'))}
            note={`${gapNote(fteGap(year), yearBudgetFte !== null, 'plan FTE', fteText)}${yearBudgetFte !== null ? ` (budget ${fte(yearBudgetFte)})` : ''}`}
            noteClass={gapClass(fteGap(year))}
          />
          <Tile
            label="AOP drafts"
            value={`${fte(fteAt(months, year, 'draftFte'))} FTE`}
            note={`${formatEuro(costAt(year, 'draftCost'))} in ${AOP_PLAN_YEAR}`}
          />
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full min-w-max border-collapse">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="sticky left-0 z-10 bg-zinc-50 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
                Targets · {label}
              </th>
              {cols.map((c) => (
                <th
                  key={c.id}
                  className={`min-w-[72px] border-l border-zinc-100 px-3 py-2 text-right text-xs font-medium uppercase tracking-wide ${
                    c.total ? 'bg-zinc-100 text-zinc-700' : 'text-zinc-500'
                  }`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {section('Headcount (FTE, end of period)')}
            {fteRow('Existing and committed', 'committedFte', 'part')}
            {fteRow('+ AOP drafts', 'draftFte', 'part')}
            {fteRow('= Plan FTE', 'planFte', 'sum')}
            {fteRow('Budget FTE', 'budgetFte', 'part')}
            {gapRow(fteGap, fteText)}
          </tbody>
          <tbody>
            {section('Cost (sum of months)')}
            {costRow('Baseline (staff and committed roles)', 'baselineCost', 'part')}
            {costRow('+ AOP drafts', 'draftCost', 'part')}
            {costRow('= Plan cost', 'planCost', 'sum')}
            {costRow('Budget cost', 'budgetCost', 'part')}
            {gapRow(costGap, formatEuro)}
          </tbody>
          {kpis.length > 0 && (
            <tbody>
              {section('Driver KPIs (per plan FTE)')}
              {kpis.map((kpi) => (
                <tr key={kpi.kpi}>
                  <th scope="row" className="sticky left-0 bg-white py-1 pl-6 pr-3 text-left text-sm font-normal text-zinc-600">
                    {kpi.kpi} per FTE{kpi.unit ? ` (${kpi.unit})` : ''}
                  </th>
                  {cols.map((c) => {
                    // A KPI per FTE is one month's figure; the FY column has none of its own.
                    const m = c.total ? undefined : kpi.byMonth[c.end];
                    return (
                      <td key={c.id} title={m?.target != null ? `Target ${plain(m.target)}` : undefined} className={cellClass(c, 'text-zinc-700')}>
                        {plain(m?.perFte)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      <div className="space-y-0.5 text-xs text-zinc-500">
        {notes}
        <p>
          FTE is read at the end of each column (December for FY &apos;{yy}), cost is the sum of its months. Plan vs
          budget is red above budget and green below. Months up to {monthLabel(AOP_HISTORY_END)} are left out here.
        </p>
      </div>
    </section>
  );
}

/** The targets for one department tab — see the module note. */
export function AopTargetsTable({
  label,
  targets,
  columns,
  groups,
  expandedGroupKeys,
}: {
  label: string;
  targets: AopDepartmentTargets;
  columns: string[];
  groups: MonthGroup[];
  expandedGroupKeys: Set<string>;
}) {
  return (
    <AopTargetsFigures
      label={label}
      months={targets.months}
      kpis={targets.kpis}
      columns={columns}
      groups={groups}
      expandedGroupKeys={expandedGroupKeys}
      notes={
        <>
          {!targets.hasTargets && (
            <p>No targets for {label} yet — Anna adds them in Airtable (AOP Targets and AOP Driver KPIs).</p>
          )}
          {targets.bandsUnavailable && (
            <p>The salary bands could not be read, so the drafts are not costed and plan cost is blank. Refresh in a moment.</p>
          )}
          {targets.costHidden && <p>Draft and plan cost are hidden: a draft here uses a salary band you can&apos;t see.</p>}
          {targets.uncostedDrafts > 0 && (
            <p>
              {targets.uncostedDrafts} {targets.uncostedDrafts === 1 ? 'draft has' : 'drafts have'} no cost yet — it
              needs a track and level, a start date and a salary band.
            </p>
          )}
          <p>
            Plan = committed FTE and cost plus this department&apos;s AOP drafts (regular proposals awaiting approval
            are not included). Hover a KPI for its target.
          </p>
        </>
      }
    />
  );
}
